"use client";

import type { ProductRecommendation } from "@/lib/api/remedies";
import { abandonPayment, getReportDownloadUrl, recordPaymentFailure } from "@/lib/api/plans";
import {
  trackPostPurchaseAgentFollowupShown,
  trackRazorpayCheckoutClosed,
  trackReportCheckoutAbandoned,
  trackReportPurchased,
  trackReportRemedyKitUpsellShown,
  trackRevenueCompleted,
  trackUpiPaymentTimeout,
} from "@/lib/analytics";
import { trackMetaPurchase } from "@/lib/checkout-event-context";
import {
  buildReportCheckoutRecoveryContext,
  buildReportRecoverySupportEmailHref,
  clearReportCheckoutRecoveryContext,
  saveReportCheckoutRecoveryContext,
} from "@/lib/checkout-recovery";
import { resolveCheckout } from "@/lib/checkout-abandon-fallback";
import { buildReportFollowupContext, saveReportFollowupContext } from "@/lib/report-followup";
import { buildShopifyCartHref, withShopifyAttribution } from "@/lib/shopify-attribution";
import type {
  CheckoutContext,
  CloseReason,
  OrderLike,
  PaymentOrder,
  RazorpayResponse,
} from "./PaidReportCTA.hook";

export function completeSuccessfulPurchase(
  ctx: CheckoutContext,
  response: RazorpayResponse,
  downloadUrl: string
) {
  const orderId = String(response.razorpay_order_id);
  // Mark the in-flight checkout as resolved so the visibilitychange /
  // beforeunload listener does not fire abandon for a paid order if
  // the user closes the tab right after seeing the success screen.
  resolveCheckout(orderId);
  trackMetaPurchase(ctx.checkoutEvent, ctx.props.priceInr);
  trackSuccess(ctx, orderId);
  ctx.props.onAgentMemoryEvent?.("purchase_completed", { priceInr: ctx.props.priceInr, orderId });
  clearReportCheckoutRecoveryContext();
  const nextDownloadUrl = getReportDownloadUrl(downloadUrl);
  const nextFollowup = buildReportFollowupContext(ctx.props.planId, orderId);
  ctx.state.setDownloadUrl(nextDownloadUrl);
  ctx.state.setFollowup(nextFollowup);
  maybeCreateRemedyKit(ctx, orderId);
  saveReportFollowupContext(nextFollowup);
  trackPostPurchase(ctx, orderId);
  window.open(nextDownloadUrl, "_blank", "noopener,noreferrer");
}

function trackSuccess(ctx: CheckoutContext, orderId: string) {
  const { props, attribution } = ctx;
  trackRazorpayCheckoutClosed(razorpayTracking(ctx, { order_id: orderId }, "payment_success"));
  trackReportPurchased({
    plan_id: props.planId,
    price_inr: props.priceInr,
    order_id: orderId,
    ...attribution,
    urgency: props.analyticsContext?.urgency,
    confidence: props.analyticsContext?.confidence,
    route_rank_score: props.analyticsContext?.routeRankScore,
    route_rank_reason: props.analyticsContext?.routeRankReason,
    ranked_offer_order: props.analyticsContext?.rankedOfferOrder,
  });
  trackRevenueCompleted({
    revenue_inr: props.priceInr,
    order_id: orderId,
    offer_type: "report",
    plan_id: props.planId,
    ...attribution,
  });
}

function maybeCreateRemedyKit(ctx: CheckoutContext, orderId: string) {
  const products = (ctx.props.remedyUpsellProducts ?? []).slice(0, 3);
  const href = remedyKitHref(ctx, products);
  if (!href || products.length === 0) return;
  ctx.state.setRemedyKit({ href, products, orderId });
  trackReportRemedyKitUpsellShown({
    plan_id: ctx.props.planId,
    order_id: orderId,
    source: ctx.props.source,
    placement: "post_report_remedy_kit",
    intent: ctx.props.analyticsContext?.intent,
    source_path: ctx.sourcePath,
    customer_problem: ctx.props.analyticsContext?.customerProblem,
    product_skus: products.map((product) => product.sku),
    product_count: products.length,
  });
}

function remedyKitHref(ctx: CheckoutContext, products: ProductRecommendation[]): string | null {
  const attribution = {
    intent: ctx.props.analyticsContext?.intent,
    problem: ctx.props.analyticsContext?.customerProblem,
    offer: products.length > 1 ? "post_report_cart" : "post_report_product",
    placement: "post_report_remedy_kit",
    confidence: ctx.props.analyticsContext?.confidence,
  };
  const sourcePath = ctx.sourcePath ?? "/";
  const cartHref = buildShopifyCartHref(products, sourcePath, attribution);
  const productUrl = products[0]?.shopify_url;
  return (
    cartHref ?? (productUrl ? withShopifyAttribution(productUrl, sourcePath, attribution) : null)
  );
}

function trackPostPurchase(ctx: CheckoutContext, orderId: string) {
  trackPostPurchaseAgentFollowupShown({
    plan_id: ctx.props.planId,
    order_id: orderId,
    source: ctx.props.source,
    placement: ctx.props.analyticsContext?.placement,
    intent: ctx.props.analyticsContext?.intent,
    source_path: ctx.sourcePath,
    customer_problem: ctx.props.analyticsContext?.customerProblem,
  });
}

export function handleDismiss(ctx: CheckoutContext, order: PaymentOrder) {
  const { props, state, attribution } = ctx;
  // Resolve the in-flight registry so the unload fallback does not
  // also fire the abandon event a second time.
  resolveCheckout(order.order_id);
  state.setPaying(false);
  state.setCheckoutAbandoned(true);
  void abandonPayment({
    razorpay_order_id: order.order_id,
    plan_id: props.planId,
    plan_type: "report",
    reading_id: ctx.readingId || undefined,
    session_id: ctx.sessionId || undefined,
    email: ctx.customerEmail || undefined,
    phone: ctx.customerPhone || undefined,
    recovery_channel: "onsite",
    recovery_url: ctx.recoveryUrl,
    recovery_email_consent: Boolean(ctx.customerEmail),
    recovery_phone_consent: false,
    ...attribution,
  }).catch(() => undefined);
  const recoveryContext = buildReportCheckoutRecoveryContext({
    planId: props.planId,
    priceInr: props.priceInr,
    razorpayOrderId: order.order_id,
    source: props.source,
    intent: props.analyticsContext?.intent,
    sourcePath: ctx.sourcePath,
    customerProblem: props.analyticsContext?.customerProblem,
  });
  saveReportCheckoutRecoveryContext(recoveryContext);
  state.setRecoveryHref(buildReportRecoverySupportEmailHref(recoveryContext));
  trackAbandoned(ctx, order.order_id);
  props.onAgentMemoryEvent?.("checkout_abandoned", { priceInr: props.priceInr });
}

function trackAbandoned(ctx: CheckoutContext, orderId: string) {
  trackReportCheckoutAbandoned({
    plan_id: ctx.props.planId,
    price_inr: ctx.props.priceInr,
    ...ctx.attribution,
    route_rank_score: ctx.props.analyticsContext?.routeRankScore,
    route_rank_reason: ctx.props.analyticsContext?.routeRankReason,
    ranked_offer_order: ctx.props.analyticsContext?.rankedOfferOrder,
  });
  trackRazorpayCheckoutClosed(razorpayTracking(ctx, { order_id: orderId }, "dismissed"));
}

export function recordFailure(
  ctx: CheckoutContext,
  order: PaymentOrder,
  response: Record<string, unknown>
) {
  // A failed payment is a terminal state — the abandon fallback should
  // not also fire when the user closes the tab after the failure modal.
  resolveCheckout(order.order_id);
  const error = (response.error ?? {}) as Record<string, unknown>;
  trackRazorpayCheckoutClosed(razorpayTracking(ctx, order, "payment_failed"));
  void recordPaymentFailure({
    razorpay_order_id: order.order_id,
    plan_id: ctx.props.planId,
    plan_type: "report",
    ...ctx.attribution,
    error_code: stringField(error.code),
    error_description: stringField(error.description),
    error_source: stringField(error.source),
    error_step: stringField(error.step),
    error_reason: stringField(error.reason),
    payment_id: paymentId(error),
  }).catch(() => undefined);

  // UPI timeout is ~94% of payment failures (the user didn't approve the
  // collect request in their UPI app in time). Razorpay already retries
  // internally before this fires; once it does, nudge toward an instant
  // method (card/netbanking) instead of leaving a dead end. Narrow detection
  // (exact `payment_timeout` reason, description fallback) so non-UPI failures
  // keep the generic flow untouched. Telemetry makes the class measurable
  // (rule 34: routed through the owned beacon, not a Cloud Run route).
  if (isUpiTimeout(error)) {
    trackUpiPaymentTimeout({
      plan_id: ctx.props.planId,
      plan_type: "report",
      order_id: order.order_id,
      source_path: ctx.attribution?.source_path,
      error_reason: stringField(error.reason),
      error_code: stringField(error.code),
    });
    ctx.state.setError(
      "UPI timed out before the payment went through. Tap pay again and choose a debit/credit card or net banking — it confirms instantly."
    );
  }
}

/** True when Razorpay's payment.failed payload is a UPI collect-request timeout. */
export function isUpiTimeout(error: Record<string, unknown>): boolean {
  const reason = stringField(error.reason).toLowerCase();
  if (reason === "payment_timeout") return true;
  const description = stringField(error.description).toLowerCase();
  return description.includes("could not be completed in time");
}

export function razorpayTracking(ctx: CheckoutContext, order: OrderLike, closeReason: CloseReason) {
  return {
    order_id: order.order_id,
    plan_id: ctx.props.planId,
    plan_type: "report",
    price_inr: ctx.props.priceInr,
    close_reason: closeReason,
    ...ctx.attribution,
  };
}

export function razorpayOpenTracking(ctx: CheckoutContext, order: OrderLike) {
  return {
    order_id: order.order_id,
    plan_id: ctx.props.planId,
    plan_type: "report",
    price_inr: ctx.props.priceInr,
    ...ctx.attribution,
  };
}

function stringField(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function paymentId(error: Record<string, unknown>): string {
  const metadata = error.metadata as Record<string, unknown> | undefined;
  return stringField(metadata?.payment_id);
}
